import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSimulation } from '../context/SimulationContext';
import { X, BookOpen, FileText, Play, BarChart3, Check, Lock, ChevronRight } from 'lucide-react';

const PHASES = [
  {
    id: 'theory',
    label: 'Conversation Tools',
    phaseLabel: 'Understand',
    description: 'Review the key conversation moves',
    icon: BookOpen,
    view: 'theory',
    requiresCompletion: null,
  },
  {
    id: 'prep',
    label: 'Conversation Plan',
    phaseLabel: 'Plan',
    description: 'Clarify your approach before you begin',
    icon: FileText,
    view: 'prep',
    requiresCompletion: null,
  },
  {
    id: 'simulation',
    label: 'Simulation',
    phaseLabel: 'Rehearse',
    description: 'Practice with Ms. Ortega',
    icon: Play,
    view: 'simulation',
    requiresCompletion: 'prepCard',
  },
  {
    id: 'dashboard',
    label: 'Debrief',
    phaseLabel: 'Reflect',
    description: 'Review your conversation outcome',
    icon: BarChart3,
    view: 'dashboard',
    requiresCompletion: 'simulation',
  },
];

export function ModuleMap({ isOpen, onClose }) {
  const { state, setView, startSimulation } = useSimulation();
  const { currentView, prepCard, outcome, simulation } = state;

  const getPhaseStatus = (phase) => {
    if (phase.id === 'theory') {
      if (state.theoryComplete) return 'complete';
      return 'available';
    }
    if (phase.id === 'prep') {
      if (prepCard.completed) return 'complete';
      return 'available';
    }
    if (phase.id === 'simulation') {
      if (simulation.active) return 'active';
      if (outcome) return 'complete';
      if (prepCard.completed) return 'available';
      return 'locked';
    }
    if (phase.id === 'dashboard') {
      if (outcome) return 'complete';
      return 'locked';
    }
    return 'available';
  };

  const handlePhaseClick = (phase) => {
    const status = getPhaseStatus(phase);
    if (status === 'locked') return;

    if (phase.id === 'simulation' && prepCard.completed && !simulation.active && !outcome) {
      startSimulation();
    } else {
      setView(phase.view);
    }
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/20 z-40"
            onClick={onClose}
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: -320, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -320, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
            className="fixed left-0 top-0 h-full w-72 sm:w-80 bg-[#002B45] text-white z-50 shadow-2xl"
            data-testid="module-map-drawer"
          >
            <div className="p-4 sm:p-8 h-full flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between mb-10">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#00A9E0] mb-1">
                    Module Map
                  </p>
                  <p className="text-sm text-white/50">
                    Your learning journey
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="text-white/30 hover:text-white transition-colors"
                  data-testid="module-map-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Phase List */}
              <div className="flex-1 space-y-2">
                {PHASES.map((phase, index) => {
                  const Icon = phase.icon;
                  const status = getPhaseStatus(phase);
                  const isActive = currentView === phase.view;
                  const isLocked = status === 'locked';
                  const isComplete = status === 'complete';

                  return (
                    <div key={phase.id}>
                      <button
                        onClick={() => handlePhaseClick(phase)}
                        disabled={isLocked}
                        className={`w-full text-left p-4 transition-all ${
                          isActive
                            ? 'bg-white/10'
                            : isLocked
                              ? 'opacity-40 cursor-not-allowed'
                              : 'hover:bg-white/5'
                        }`}
                        data-testid={`module-map-${phase.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-8 h-8 flex items-center justify-center ${
                            isComplete ? 'bg-[#78BE20]' :
                            isActive ? 'bg-[#00A9E0]' :
                            isLocked ? 'bg-white/10' :
                            'bg-white/10'
                          }`}>
                            {isComplete ? (
                              <Check className="w-4 h-4 text-white" strokeWidth={2} />
                            ) : isLocked ? (
                              <Lock className="w-3.5 h-3.5 text-white/40" strokeWidth={1.5} />
                            ) : (
                              <Icon className="w-4 h-4 text-white" strokeWidth={1.5} />
                            )}
                          </div>

                          <div className="flex-1">
                            <p className={`text-[10px] uppercase tracking-widest mb-0.5 ${
                              isActive ? 'text-[#00A9E0]' : 'text-white/30'
                            }`}>
                              {phase.phaseLabel}
                            </p>
                            <p className={`text-sm font-medium ${
                              isActive ? 'text-white' : 'text-white/70'
                            }`}>
                              {phase.label}
                            </p>
                            <p className="text-xs text-white/30 mt-0.5">
                              {phase.description}
                            </p>
                          </div>

                          {!isLocked && (
                            <ChevronRight className="w-4 h-4 text-white/20" />
                          )}
                        </div>
                      </button>

                      {/* Connector line */}
                      {index < PHASES.length - 1 && (
                        <div className="ml-8 pl-4">
                          <div className={`w-px h-4 ${
                            isComplete ? 'bg-[#78BE20]' : 'bg-white/10'
                          }`} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Footer status */}
              <div className="pt-6 border-t border-white/10">
                <p className="text-[10px] uppercase tracking-widest text-white/30 mb-2">
                  Current Phase
                </p>
                <p className="text-sm text-white/70">
                  {currentView === 'hub' ? 'Dashboard' :
                   currentView === 'theory' ? 'Conversation Tools' :
                   currentView === 'prep' ? 'Conversation Plan' :
                   currentView === 'simulation' ? 'Simulation' :
                   currentView === 'dashboard' ? 'Debrief' :
                   'Dashboard'}
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export default ModuleMap;
